/**
 * Coaching API helpers.
 */

import { z } from "zod";
import { apiClient } from "../client";
import {
  CoachingSession,
  CoachingSessionSchema,
  CoachingQuestion,
  CoachingQuestionSchema,
  CoachingResponse,
  CoachingResponseSchema,
  CoachingSessionSummary,
  CoachingSessionSummarySchema,
  CoachingMode,
  DashboardStatus,
  DashboardStatusSchema,
  PendingCoachingEdit,
  PendingCoachingEditSchema,
} from "../../validators/coaching";

export interface CreateSessionParams {
  mode: CoachingMode;
  target_job_ad?: string;
  target_job_ad_uuid?: string;
}

export async function createCoachingSession(
  params: CreateSessionParams
): Promise<CoachingSession> {
  const res = await apiClient.post("/api/coaching/sessions/", params);
  return CoachingSessionSchema.parse(res.data);
}

export async function getCoachingSession(uuid: string): Promise<CoachingSession> {
  const res = await apiClient.get(`/api/coaching/sessions/${uuid}/`);
  return CoachingSessionSchema.parse(res.data);
}

export async function listCoachingSessions(params?: {
  mode?: CoachingMode;
  target_job_ad?: string;
  status?: string;
}): Promise<CoachingSession[]> {
  const search = new URLSearchParams();
  if (params?.mode) search.set("mode", params.mode);
  if (params?.target_job_ad) search.set("target_job_ad", params.target_job_ad);
  if (params?.status) search.set("status", params.status);
  const qs = search.toString() ? `?${search.toString()}` : "";
  const res = await apiClient.get(`/api/coaching/sessions/${qs}`);
  return z.array(CoachingSessionSchema).parse(res.data);
}

export async function getCoachingQuestions(
  sessionUuid: string
): Promise<CoachingQuestion[]> {
  const res = await apiClient.get(`/api/coaching/sessions/${sessionUuid}/questions/`);
  return z.array(CoachingQuestionSchema).parse(res.data);
}

export async function pauseCoachingSession(uuid: string): Promise<CoachingSession> {
  const res = await apiClient.post(`/api/coaching/sessions/${uuid}/pause/`);
  return CoachingSessionSchema.parse(res.data);
}

export async function resumeCoachingSession(uuid: string): Promise<CoachingSession> {
  const res = await apiClient.post(`/api/coaching/sessions/${uuid}/resume/`);
  return CoachingSessionSchema.parse(res.data);
}

export async function completeCoachingSession(uuid: string): Promise<CoachingSession> {
  const res = await apiClient.post(`/api/coaching/sessions/${uuid}/complete/`);
  return CoachingSessionSchema.parse(res.data);
}

export async function abandonCoachingSession(uuid: string): Promise<CoachingSession> {
  const res = await apiClient.post(`/api/coaching/sessions/${uuid}/abandon/`);
  return CoachingSessionSchema.parse(res.data);
}

export async function getSessionSummary(
  sessionUuid: string
): Promise<CoachingSessionSummary> {
  const res = await apiClient.get(
    `/api/coaching/sessions/${sessionUuid}/summary/`
  );
  return CoachingSessionSummarySchema.parse(res.data);
}

export async function listPendingCoachingEdits(
  sessionUuid?: string
): Promise<PendingCoachingEdit[]> {
  const qs = sessionUuid ? `?session=${sessionUuid}` : "";
  const res = await apiClient.get(`/api/coaching/pending-edits/${qs}`);
  return z.array(PendingCoachingEditSchema).parse((res.data as { edits: unknown[] }).edits);
}

export async function acceptCoachingEdit(proposalUuid: string): Promise<void> {
  await apiClient.post(`/api/coaching/pending-edits/${proposalUuid}/accept/`);
}

export async function rejectCoachingEdit(proposalUuid: string): Promise<void> {
  await apiClient.post(`/api/coaching/pending-edits/${proposalUuid}/reject/`);
}

export interface SubmitResponseParams {
  response_payload: Record<string, unknown>;
  raw_user_text: string;
}

export async function submitQuestionResponse(
  questionUuid: string,
  params: SubmitResponseParams
): Promise<CoachingResponse> {
  const res = await apiClient.post(
    `/api/coaching/questions/${questionUuid}/respond/`,
    params
  );
  return CoachingResponseSchema.parse(res.data);
}

export async function skipQuestion(questionUuid: string): Promise<void> {
  await apiClient.post(`/api/coaching/questions/${questionUuid}/skip/`);
}

export async function declineQuestion(questionUuid: string): Promise<void> {
  await apiClient.post(`/api/coaching/questions/${questionUuid}/decline/`);
}

export async function dontKnowQuestion(questionUuid: string): Promise<void> {
  await apiClient.post(`/api/coaching/questions/${questionUuid}/dont-know/`);
}

export async function getCoachingDashboardStatus(): Promise<DashboardStatus> {
  const res = await apiClient.get("/api/coaching/dashboard-status/");
  return DashboardStatusSchema.parse(res.data);
}
